import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import EmployeeTable from "../components/employee/EmployeeTable";
import { useStores } from "../../use-stores";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { GetEmployeeParams } from "../../models/Employees/GetEmployeeParams";
import { SortType } from "../../models/SortType";
import EditEmployeePopup from "../components/employee/EditEmployeePopup";
import ConfirmPopup from "../components/common/pop-up/ConfirmPopup";
import { Employee, Resetpassword } from "../../models/Employees/Employee";
import { useNotification } from "../../utils/NotificationsContext";
import FormTextField from "../components/common/form/FormTextField";
import AppButton from "../components/common/buttons/AppButton";
import CustomTypography from "../components/common/form/CustomTypography";
import AppDropDown from "../components/common/filter/AppDropDown";
import { FilterValue } from "../../models/Filters/FilterValue";

const employeeStatusFilterOptions: FilterValue[] = [
  { id: "all", value: "Select" },
  { id: "true", value: "Active" },
  { id: "false", value: "Inactive" },
];

const EmployeeContent: React.FC = () => {
  const { employeeStore, userStore } = useStores();
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);

  const initialParams: GetEmployeeParams = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sortBy: searchParams.get("sort_by") || "name",
    sortType:
      searchParams.get("sort_type") === "ASC" ? SortType.DESC : SortType.ASC,
    search_text: searchParams.get("search_text") || "",
    active: searchParams.get("active")
      ? searchParams
        .get("active")!
        .split(",")
          .map((value) => {
            if (value === "true") return 1;
            if (value === "false") return 0;
            const num = parseInt(value, 10);
            return Number.isNaN(num) ? undefined : num;
          })
          .filter((value): value is number => value !== undefined)
      : undefined,
  };

  const [params, setParams] = useState(initialParams);
  const [selectedStatusId, setSelectedStatusId] = useState<string>(() => {
    const active = initialParams.active;
    if (active && active.length > 0) return active[0].toString();
    return "all";
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [addEmployee, setAddEmployee] = useState<boolean>(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  useEffect(() => {
    setIsLoading(true);
    employeeStore
      .getEmployee(params)
      .catch((reason) =>
        showError(
          "Error!",
          reason?.response?.data?.message ||
          "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, employeeStore]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value && (!Array.isArray(value) || value.length > 0)) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
  }, [params, navigate]);

  const handleSearchBar = (value: string | undefined) => {
    if (value && value.length !== 0) {
      setSearchTerm(value);
    } else {
      setSearchTerm("");
    }
  };

  const handleSearchSubmit = () => {
    setParams((prev) => ({
      ...prev,
      search_text: searchTerm.trim() || "",
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({ ...prev, search_text: "" }));
      }
    }
  };

  const applyFilters = () => {
    setParams((prev) => ({
      ...prev,
      page_no: 1,
      active:
        selectedStatusId === "all"
          ? undefined
          : [selectedStatusId === "true" ? 1 : 0],
    }));
  };

  const clearAll = () => {
    setSearchTerm("");
    setSelectedStatusId("all");
    setParams((prev) => ({
      ...prev,
      search_text: "",
      page_no: 1,
      active: undefined,
    }));
  };
  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleEditEmployee = (
    updatedEmployee: Employee,
    onClose: () => void,
  ) => {
    employeeStore
      .editEmployee(updatedEmployee.id!, updatedEmployee!)
      .then((res) => {
        if (res?.success) {
          showSuccess("Employee data updated successfully!");
          onClose();
        } else {
          showError(
            "Error!",
            res?.message || "Something went wrong! Please try again later.",
          );
        }
      })
      .catch((reason) =>
        showError(
          "Error!",
          reason?.response?.data?.message ||
          "Something went wrong! Please try again later.",
        ),
      );
  };

  const handleEditClick = async (employeeId: number) => {
    try {
      await employeeStore.getEmployeeDetails(employeeId);
      await employeeStore.getTeamPermission(employeeId);
    } catch (err) {
      showError(
        "Error!",
        "Failed to fetch employee details! Please make sure you have proper rights to fetch other employee details",
      );
    }

    // if (employeeStore.getTeam.length === 0)
    //   employeeStore
    //     .getAllTeam()
    //     .catch((err) =>
    //       showError(
    //         "Error!",
    //         err?.response?.data?.message ||
    //           "Something went wrong! Please try again later."
    //       )
    //     );
  };

  const handleViewClick = async (employeeId: number) => {
    try {
      await employeeStore.getEmployeeDetails(employeeId);
      await employeeStore.getTeamPermission(employeeId);
    } catch (err) {
      showError(
        "Error!",
        "Failed to fetch employee details! Please make sure you have proper rights to fetch other employee details",
      );
    }
  };

  const handleDeleteClick = (employeeId: number) => {
    setConfirmDeleteId(employeeId);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setIsDeleting(true);
      const res = await employeeStore.deleteEmployee(confirmDeleteId);
      if ((res as any)?.success) {
        showSuccess("Employee deleted successfully!");
        await employeeStore.getEmployee(params);
      } else {
        showError(
          "Error!",
          (res as any)?.message ||
          "Failed to delete employee! Please try again later.",
        );
      }
    } catch (err: any) {
      showError(
        "Error!",
        err?.response?.data?.message ||
        "Failed to delete employee! Please make sure you have proper rights to delete other employee",
      );
    } finally {
      setIsDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const handleResetPwd = (email: Resetpassword) => {
    employeeStore
      .resetPassword(email)
      .then((res) => {
        if (res?.success) {
          showSuccess("Reset password link sent successfully");
          setAddEmployee(false);
        }
      })
      .catch((reason) =>
        showError(
          "Error!",
          reason?.response?.data?.message ||
          "Something went wrong! Please try again later.",
        ),
      );
  };
  const handleCreateEmployee = (Employee: Employee) => {
    employeeStore
      .addEmployee(Employee)
      .then((res) => {
        if (res?.success) {
          showSuccess("Employee created successfully!");
          setAddEmployee(false);
          employeeStore.getEmployee(params).finally(() => {
            setIsLoading(false);
          });
        }
      })
      .catch((reason) =>
        showError(
          "Error!",
          reason?.response?.data?.message ||
          "Something went wrong! Please try again later.",
        ),
      );
  };

  useEffect(() => {
    if (addEmployee) {
      employeeStore.getAllTeam();
    }
  }, [addEmployee, employeeStore]);

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      <Paper
        sx={{
          mt: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow:
            "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 1,
            }}
          >
            <Box>
              <CustomTypography variant="heading3">Employees</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View and manage employees. Search by name or email.
              </CustomTypography>
            </Box>
            {userStore.isSuperAdminManager() && (
              <AppButton
                variant="primary"
                label="Add Employee"
                onClick={() => setAddEmployee(true)}
                customStyles={{ minWidth: 140, height: 40 }}
              />
            )}
          </Box>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            borderRadius: 2,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 0.5,
              cursor: "pointer",
              borderBottom: isSearchExpanded ? "1px solid" : "none",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CustomTypography variant="heading6">Search</CustomTypography>
            <IconButton size="small">
              {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  alignItems: { xs: "stretch", sm: "center" },
                  marginBottom: { xs: 2.5, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: { xs: "100%", sm: 280 },
                  }}
                >
                  <FormTextField
                    id="employee-search"
                    placeholder="Search by employee name or email"
                    type="search"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(_, value) => handleSearchBar(value)}
                    onKeyDown={(e) => handleKeyDown(e)}
                    sx={{ mb: 0 }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    justifyContent: { xs: "flex-end", sm: "flex-start" },
                    alignSelf: { xs: "flex-end", sm: "auto" },
                  }}
                >
                  <AppButton
                    variant="primary"
                    label="Search"
                    onClick={handleSearchSubmit}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear All"
                    onClick={clearAll}
                    customStyles={{ minWidth: 140, height: 40 }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 220,
                    maxWidth: "100%",
                  }}
                >
                  <AppDropDown
                    title="Status"
                    isMultiSelect={false}
                    value={selectedStatusId}
                    list={employeeStatusFilterOptions}
                    fontSize="12px"
                    onSingleSelectionChange={(id) => {
                      setSelectedStatusId(id);
                    }}
                  />
                </Box>
                <Box sx={{ pt: { xs: 0, sm: 0.25 } }}>
                  <AppButton
                    variant="primary"
                    label="Apply"
                    onClick={applyFilters}
                    customStyles={{ minWidth: 140, height: 37, minHeight: 37 }}
                  />
                </Box>
              </Box>
            </Box>
          </Collapse>
        </Paper>

        {isLoading && (
          <Container
            sx={{
              minHeight: "40vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Container>
        )}

        {!isLoading && (
          <Box mt={3}>
            {employeeStore.employees.length === 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="10rem"
                textAlign="center"
                border="1px dashed"
                borderColor="divider"
                borderRadius={2}
              >
                <CustomTypography
                  variant="heading6"
                  fontWeight={500}
                  color={theme.palette.customColors?.greyTextColor}
                >
                  No employees match the current search criteria.
                </CustomTypography>
              </Box>
            )}
            {employeeStore.employees.length > 0 && (
              <EmployeeTable
                employees={employeeStore.employees || []}
                limit={params.limit || employeeStore.limit}
                page_no={params.page_no}
                total_count={employeeStore.total_count}
                total_pages={employeeStore.total_pages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onEditClick={handleEditClick}
                onViewClick={handleViewClick}
                onDeleteClick={handleDeleteClick}
              />
            )}
          </Box>
        )}
      </Paper>

      {employeeStore.employeeDetails && employeeStore.teamPermissions && (
        <EditEmployeePopup
          open={true}
          employeeData={employeeStore.employeeDetails}
          onClose={() => {
            employeeStore.removeEmployeeDetails();
          }}
          onSave={(updatedEmployee) =>
            handleEditEmployee(updatedEmployee, () =>
              employeeStore.removeEmployeeDetails(),
            )
          }
          employeePermissionList={employeeStore.teamPermissions}
          employeeTeamList={employeeStore.getTeam}
          canEditTeam={userStore.isSuperAdminManager()}
          isViewOnly={
            !(
              userStore.isSuperAdminManager() ||
              userStore.isTeamManager(
                employeeStore.employeeDetails.teams?.map((team) => team.id),
              )
            )
          }
          discountPermission={
            !(
              userStore.isSuperAdminManager() &&
              userStore.isTeamManager(
                employeeStore.employeeDetails.teams?.map((team) => team.id),
              )
            )
          }
          resetPw={(email) => handleResetPwd(email)}
        />
      )}
      {addEmployee && (
        <EditEmployeePopup
          open={true}
          isAddMode={true}
          onClose={() => {
            setAddEmployee(false);
          }}
          onSave={(Employee) => {
            handleCreateEmployee(Employee);
          }}
          discountPermission={
            !(
              userStore.isSuperAdminManager() ||
              userStore.isTeamManager(
                employeeStore.employeeDetails?.teams?.map((team) => team.id) ||
                [],
              )
            )
          }
          employeeTeamList={employeeStore.getTeam}
          isSuperAdminManager={userStore.isSuperAdminManager()}
        />
      )}
      <ConfirmPopup
        open={confirmDeleteId !== null}
        title="Delete employee?"
        message="This action will permanently remove the employee. Do you want to proceed?"
        cancelText="Cancel"
        confirmText="Apply"
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
      />
    </Grid2>
  );
};

export default observer(EmployeeContent);
